import React, { useState } from "react"
import PropTypes from "prop-types"
import { AnchorLink } from "gatsby-plugin-anchor-links"
import { StaticImage } from "gatsby-plugin-image"
import { HiCheck } from "react-icons/hi"
import HelpText from "./form/HelpText"
import Input from "./form/Input"
import { useForm } from "react-hook-form"
import { fbSimpleConversionEventApi } from "../libs/function"
import EdmModal from "./modal/EdmModal"
import { navigate } from "gatsby"
import getDate from "../libs/getDate"
import CustomColorRing from "./form/ColorRing"
import { edmCheck, sendToGoogleSheet } from "../libs/function"
import { SendToGoogleSheetId } from "../libs/getSheetId"

const Cta = ({
  className = "my-12",
  destination = "/contact#collapseTop",
  title = "立即諮詢",
  isEDM = false,
}) => {
  const [isPending, setIsPending] = useState(false)
  const [isModal, setIsModal] = useState(false)

  const curUrl = typeof window !== "undefined" ? window.location.pathname : null

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm()

  const onSubmit = async data => {
    try {
      const { name, email } = data

      await setIsPending(true)
      const currentTime = getDate()

      const body = JSON.stringify({
        name,
        email,
        dayOfCycle: 0,
        campaign: {
          campaignId: "qvwPF",
        },
      })
      const formData = {
        rows: {
          name: name,
          email: email,
          type: "CTA",
          slug: curUrl,
          date: currentTime,
        },
        docId: SendToGoogleSheetId.default,
        sheetId: "281173528", // 工作表名稱：EDM訂閱名單
      }

      // 透過edm.js檢查是否重複或格式有誤
      const result = await edmCheck(body)

      // 若回傳status===200，則寫入google-sheet
      if (result.status === 200) {
        await sendToGoogleSheet(formData)

        await setIsPending(false)
        await reset()
        setIsModal(true)
        await navigate(`${curUrl}?edmSuccess=true`)
      } else if (result.status === 400) {
        await setIsPending(false)
        alert("信箱格式錯誤")
      } else if (result.status === 409) {
        await setIsPending(false)
        alert("您的 email 已經訂閱，若有問題請加 LINE 詢問")
      } else {
        await setIsPending(false)
        alert("發生錯誤! 請加line聯繫我們")
      }
    } catch (e) {
      await setIsPending(false)
      alert("發生不可預期的錯誤! 請加line聯繫我們")
    }
  }

  const learningResources = [
    "每週一篇新知報",
    "一季一本電子書",
    "SEO 系統性學習文",
  ]
  const learningResourcesBlock = learningResources.map((resource, index) => {
    return (
      <div key={index} className="flex">
        <HiCheck className="text-yellow-400 mr-1 w-7 h-7 sm:w-[30px] sm:h-[30px] lg:w-[26px] lg:h-[26px]" />
        <p className="text-lg leading-normal text-white lg:text-base">
          {resource}
        </p>
      </div>
    )
  })

  return isEDM ? (
    <div
      className={`mx-auto w-full lg:grid lg:grid lg:grid-cols-5 lg:h-96 ${className}`}
    >
      <StaticImage
        src="../images/img_EDM_CTA_M_T.png"
        className="aspect-video lg:!hidden"
        alt="img_CTA"
        placeholder="blurred"
        quality={100}
      />
      <StaticImage
        src="../images/img_EDM_CTA_D.png"
        className="!hidden lg:!inline-block lg:col-span-2"
        alt="img_CTA"
        placeholder="blurred"
        quality={100}
      />
      <div className="bg-gray-900 text-white py-6 px-6 sm:p-16 lg:py-0 lg:pr-0 lg:col-span-3 lg:flex lg:flex-col lg:justify-start">
        <div className="lg:flex lg:flex-col lg:items-start lg:justify-center lg:h-full">
          <p className="leading-tight text-3xl text-center font-bold sm:text-4xl sm:text-left">
            想收到 Welly 彙整的國內外行銷新知？
          </p>
          <div className="mx-auto flex flex-col max-w-[192px] gap-3 sm:flex-row sm:max-w-none sm:mt-3 sm:mb-6 sm:items-center sm:ml-0">
            <p className="text-lg mt-6 leading-normal whitespace-nowrap text-center sm:leading-tight sm:mt-0 lg:text-base">
              訂閱可以收到：
            </p>
            {learningResourcesBlock}
          </div>
          {/* 訂閱區塊 */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="grid grid-cols-1 col-span-2 gap-4 mt-6 sm:mt-0 sm:justify-self-stretch lg:max-w-lg lg:w-full"
            id="collapseTop"
          >
            <input
              {...register("name", {
                required: true,
                minLength: 2,
                maxLength: 128,
              })}
              placeholder="希望 Welly 如何稱呼您？"
              className={`w-full h-10 pl-4 text-lg outline-none tracking-wide text-gray-900 border-b border-b-gray-900 placeholder:text-gray-400 focus:border-b-2 ${
                errors["name"] ? "border-b-yellow-500" : ""
              }`}
            />
            {errors["name"] && <HelpText text="*此為必填項目(兩字以上)" />}
            <Input
              name="email"
              register={register}
              isRequired={true}
              placeholder="您常用的 Email"
              errors={errors["email"]}
            />
            {errors["email"] && <HelpText text="*此為必填項目" />}
            {isPending ? (
              <CustomColorRing />
            ) : (
              <button
                type="submit"
                className="block mx-auto py-2 px-4 font-medium text-gray-900 bg-yellow-300 transition ease-in-out duration-150 hover:bg-yellow-400 active:bg-yellow-500 sm:py-3 sm:px-6 sm:text-xl lg:ml-0"
              >
                訂閱電子報
              </button>
            )}
          </form>
        </div>
      </div>
      {isModal && <EdmModal setIsModal={setIsModal} />}
    </div>
  ) : (
    <div
      className={`mx-auto w-full flex flex-col lg:grid lg:grid lg:grid-cols-5 lg:h-96 ${className}`}
    >
      <StaticImage
        src="../images/img_CTA.jpg"
        className="order-2 lg:order-1 aspect-video lg:col-span-2 lg:aspect-auto"
        alt="img_CTA"
        placeholder="blurred"
        quality={100}
      />
      <div className="order-1 lg:order-2 bg-gray-900 text-white py-12 px-6 sm:p-16 lg:py-0 lg:pr-0 lg:col-span-3 lg:flex lg:flex-col lg:justify-start">
        <div className="lg:flex lg:flex-col lg:items-start lg:justify-center lg:h-full">
          <p className="leading-tight text-center text-3xl font-bold sm:text-5xl sm:leading-loose lg:text-left">
            台灣最專業的 SEO 行銷團隊！
          </p>
          <p className="text-lg mt-3 mb-8 leading-normal text-center sm:text-2xl sm:leading-tight lg:text-left">
            現在就與 Welly 一起將目標關鍵字攻上 Google 首頁吧！
          </p>
          <AnchorLink
            onAnchorLinkClick={async () =>
              await fbSimpleConversionEventApi("Contact")
            }
            to={destination}
            stripHash
          >
            <button
              type="button"
              className="convert block mx-auto text-gray-900 bg-yellow-300 hover:bg-yellow-400 active:bg-yellow-500 transition ease-in-out duration-150 py-2 px-4 font-medium sm:py-3 sm:px-5 sm:text-xl"
            >
              {title}
            </button>
          </AnchorLink>
        </div>
      </div>
    </div>
  )
}

Cta.propTypes = {
  className: PropTypes.string,
  destination: PropTypes.string,
  title: PropTypes.string,
  isEDM: PropTypes.bool,
}

export { Cta }
